import { useGame, usePlayer, useRound, useStage } from "@empirica/core/player/classic/react";

import React, { useEffect } from "react";
import { Button } from "./components/Button";
import { Profile } from "./Profile";
import { Stage } from "./Stage";
import {
  PartnerRemovedForInactivityStage,
  RemovedForInactivityStage,
} from "./intro-exit/InactivityExitStage";
import { NegotiationChat } from "./negotiation/NegotiationChat";
import { useTaskMaster } from "./taskmaster/useTaskMaster";

const INTERACTIVE_SELECTOR =
  "input, textarea, select, button, [contenteditable='true'], [role='textbox'], [role='slider'], [role='spinbutton']";

function closestElement(target, selector) {
  return target?.closest?.(selector) || null;
}

function isNoInteractionTarget(target) {
  return Boolean(closestElement(target, "[data-no-interaction]"));
}

function isInteractiveTarget(target) {
  return Boolean(closestElement(target, INTERACTIVE_SELECTOR));
}

function isMeaningfulInteractionKey(event) {
  if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
    return true;
  }

  return ["Enter", " ", "Backspace", "Delete"].includes(event.key);
}

function requestId(playerId) {
  const randomPart =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(16).slice(2);

  return `${playerId || "player"}-${Date.now()}-${randomPart}`;
}

function TestStageNavigation({ disabled, onNavigate }) {
  return (
    <div
      className="fixed right-4 top-4 z-50 flex items-center overflow-hidden rounded-md border border-gray-300 bg-white shadow-sm"
      data-no-interaction
      aria-label="Test page navigation"
    >
      <button
        type="button"
        className="h-9 w-10 border-r border-gray-300 text-xl font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-empirica-500 disabled:cursor-not-allowed disabled:opacity-40"
        disabled={disabled}
        onClick={() => onNavigate("previous")}
        aria-label="Previous page"
        title="Previous page"
      >
        &larr;
      </button>
      <button
        type="button"
        className="h-9 w-10 text-xl font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-empirica-500 disabled:cursor-not-allowed disabled:opacity-40"
        disabled={disabled}
        onClick={() => onNavigate("next")}
        aria-label="Next page"
        title="Next page"
      >
        &rarr;
      </button>
    </div>
  );
}

export function Game() {
  const game = useGame();
  const player = usePlayer();
  const round = useRound();
  const stage = useStage();
  const { playerCount } = game.get("treatment");
  const task = round?.get("task");
  const design = game.get("design") || {};
  const testMode = design.testMode === true;
  const inactivityRemoval = game.get("inactivityRemoval");
  const participantExitStatus = player.get("participantExitStatus");
  const negotiation = game.get("negotiation") || {};
  const initialOffersCleared =
    negotiation.preDiscussionAgreement?.status === "agreed";
  const isNegotiationDiscussionRound = task === "negotiation_discussion";
  const removedForInactivity =
    inactivityRemoval?.removedPlayerIds?.includes(player.id) ||
    inactivityRemoval?.removedPlayerId === player.id ||
    participantExitStatus === "removed_for_inactivity";
  const partnerRemovedForInactivity =
    inactivityRemoval?.partnerPlayerId === player.id ||
    participantExitStatus === "partner_removed_for_inactivity";
  const hasInactivityExit =
    Boolean(inactivityRemoval) || removedForInactivity || partnerRemovedForInactivity;
  const showChat =
    !hasInactivityExit &&
    playerCount > 1 &&
    isNegotiationDiscussionRound &&
    !initialOffersCleared;
  const stageSubmitted = stage ? player.stage.get("submit") === true : false;

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [round?.get("name"), stage?.get("name")]);

  useTaskMaster({
    enabled:
      Boolean(stage && round) &&
      !stageSubmitted &&
      !removedForInactivity &&
      !partnerRemovedForInactivity,
    game,
    player,
    round,
    stage,
  });

  function markStageInteraction() {
    if (!stage || player.stage.get("submit")) {
      return false;
    }

    player.stage.set("interacted", true);
    return true;
  }

  function handleClickCapture(event) {
    const clickedButton = closestElement(event.target, "button");
    if (!clickedButton || isNoInteractionTarget(event.target)) {
      return;
    }

    markStageInteraction();
  }

  function handleInputCapture(event) {
    if (isNoInteractionTarget(event.target) || !isInteractiveTarget(event.target)) {
      return;
    }

    markStageInteraction();
  }

  function handleKeyDownCapture(event) {
    if (
      isNoInteractionTarget(event.target) ||
      !isInteractiveTarget(event.target) ||
      !isMeaningfulInteractionKey(event)
    ) {
      return;
    }

    markStageInteraction();
  }

  function handleContinueWithoutInteraction() {
    if (!stage || player.stage.get("submit")) {
      return;
    }

    player.stage.set("submit", true);
  }

  function handleTestStageNavigation(direction) {
    if (!stage || !game) {
      return;
    }

    game.set("testStageNavigationRequest", {
      id: requestId(player.id),
      direction,
      playerId: player.id,
      stageId: stage.id,
    });
  }

  let stageContent = <Stage />;
  if (removedForInactivity) {
    stageContent = <RemovedForInactivityStage />;
  } else if (partnerRemovedForInactivity) {
    stageContent = <PartnerRemovedForInactivityStage />;
  }

  return (
    <div
      className="h-full w-full flex"
      onClickCapture={handleClickCapture}
      onInputCapture={handleInputCapture}
      onChangeCapture={handleInputCapture}
      onKeyDownCapture={handleKeyDownCapture}
    >
      <div className={`h-full w-full flex flex-col ${showChat ? "pr-160" : ""}`}>
        <Profile />
        <div
          className={`flex min-h-0 flex-1 items-start justify-center ${
            showChat ? "negotiation-info-scrollbar overflow-y-scroll" : ""
          }`}
        >
          {stageContent}
        </div>
        {testMode && !hasInactivityExit ? (
          <div className="fixed bottom-4 right-4 z-50" data-no-interaction>
            <Button
              type="button"
              primary
              disabled={!stage || player.stage.get("submit")}
              handleClick={handleContinueWithoutInteraction}
            >
              Continue without interaction (test)
            </Button>
          </div>
        ) : null}
        {testMode && !hasInactivityExit ? (
          <TestStageNavigation
            disabled={!stage}
            onNavigate={handleTestStageNavigation}
          />
        ) : null}
      </div>

      {showChat && (
        <div className="fixed top-0 right-0 h-screen w-160 border-l bg-white">
          <NegotiationChat />
        </div>
      )}
    </div>
  );
}
